import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useAuth } from '../lib/auth';
import {
  fetchAllDistributions,
  fetchBundle,
  fetchPredictions,
  fetchRules,
  savePrediction,
  type Distribution,
} from '../lib/gameData';
import {
  type Bundle,
  type BundleMatch,
  type Prediction,
  type Rules,
  DEFAULT_RULES,
  isLive,
  scorePrediction,
} from '../lib/wcTypes';

type GameState = {
  bundle: Bundle | null;
  rules: Rules;
  predictions: Map<number, Prediction>;
  distributions: Map<number, Distribution>;
  loading: boolean;
  /** Active boost tokens already spent across the slate. */
  boostsUsed: number;
  pickFor: (n: number) => Prediction | undefined;
  pointsFor: (n: number) => number;
  /** Optimistic save; rolls back and rethrows on error. */
  submitPick: (n: number, body: { scoreA: number; scoreB: number; boost: boolean }) => Promise<void>;
  refreshBundle: () => Promise<void>;
};

const Ctx = createContext<GameState | null>(null);

// Routes that consume game data — used to lazily fetch the bundle only when
// the user is actually inside the app (not on the marketing landing/admin).
const APP_ROUTES = new Set(['play', 'boards', 'profile', 'info', 'partners', 'match', 'onboarding']);
function inApp(path: string): boolean {
  const segs = path.split('/').filter(Boolean);
  return segs.some((s) => APP_ROUTES.has(s));
}

export function GameDataProvider({ children }: { children: ComponentChildren }) {
  const { status, accessToken, hydrate } = useAuth();
  const loc = useLocation();
  const active = inApp(loc.path);
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [rules, setRules] = useState<Rules>(DEFAULT_RULES);
  const [predictions, setPredictions] = useState<Map<number, Prediction>>(new Map());
  const [distributions, setDistributions] = useState<Map<number, Distribution>>(new Map());
  const [loading, setLoading] = useState(true);
  const startedRef = useRef(false);

  const tokenRef = useRef<string | null>(accessToken);
  tokenRef.current = accessToken;
  const auth = useMemo(() => ({ getToken: () => tokenRef.current, hydrate }), [hydrate]);

  const refreshBundle = useCallback(async () => {
    try {
      const b = await fetchBundle();
      setBundle(b);
    } catch {
      /* keep last good bundle */
    }
  }, []);

  // Public data — fetched once, the first time the user enters an app route.
  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;
    let alive = true;
    (async () => {
      const [b, r] = await Promise.all([fetchBundle(), fetchRules()]);
      if (!alive) return;
      setBundle(b);
      setRules(r);
      setLoading(false);
      fetchAllDistributions().then((d) => alive && setDistributions(d)).catch(() => {});
    })();
    return () => {
      alive = false;
    };
  }, [active]);

  // Predictions track the session (only while inside the app).
  useEffect(() => {
    if (status !== 'signed-in') {
      setPredictions(new Map());
      return;
    }
    if (!active) return;
    let alive = true;
    fetchPredictions(auth)
      .then((m) => alive && setPredictions(m))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [status, accessToken, active]);

  // Adaptive bundle polling: fast when something is live, lazy otherwise.
  // Only while the user is inside the app.
  useEffect(() => {
    if (!bundle || !active) return;
    const anyLive = bundle.matches.some(isLive);
    const ms = anyLive ? 60_000 : 5 * 60_000;
    const id = setInterval(refreshBundle, ms);
    return () => clearInterval(id);
  }, [bundle, active, refreshBundle]);

  const boostsUsed = useMemo(() => {
    let n = 0;
    for (const p of predictions.values()) if (p.boost) n++;
    return n;
  }, [predictions]);

  const matchByNumber = useMemo(() => {
    const map = new Map<number, BundleMatch>();
    if (bundle) for (const m of bundle.matches) map.set(m.number, m);
    return map;
  }, [bundle]);

  const pickFor = useCallback((n: number) => predictions.get(n), [predictions]);

  const pointsFor = useCallback(
    (n: number) => {
      const m = matchByNumber.get(n);
      if (!m) return 0;
      return scorePrediction(m, predictions.get(n), rules);
    },
    [matchByNumber, predictions, rules],
  );

  const submitPick = useCallback(
    async (n: number, body: { scoreA: number; scoreB: number; boost: boolean }) => {
      const prev = predictions.get(n);
      // Optimistic.
      setPredictions((m) => {
        const next = new Map(m);
        next.set(n, { matchNumber: n, ...body });
        return next;
      });
      try {
        const saved = await savePrediction(auth, n, body);
        setPredictions((m) => {
          const next = new Map(m);
          next.set(n, saved);
          return next;
        });
      } catch (e) {
        // Roll back.
        setPredictions((m) => {
          const next = new Map(m);
          if (prev) next.set(n, prev);
          else next.delete(n);
          return next;
        });
        if ((e as { code?: string }).code === 'match_locked') refreshBundle();
        throw e;
      }
    },
    [auth, predictions, refreshBundle],
  );

  const value: GameState = {
    bundle,
    rules,
    predictions,
    distributions,
    loading,
    boostsUsed,
    pickFor,
    pointsFor,
    submitPick,
    refreshBundle,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGameData(): GameState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useGameData must be used within GameDataProvider');
  return v;
}
